export const FOOD_OPTION_KEYS = ["vegetarian", "vegan", "glattKosher", "glutenFree"];

const FOOD_DISH_LABELS = {
  vegetarian: "צמחונית",
  vegan: "טבעונית",
  glattKosher: "גלאט כשר",
  glutenFree: "ללא גלוטן",
};

export const RSVP_OPTION_LABELS = {
  transportation: "הסעה",
  vegetarian: "מנה צמחונית",
  vegan: "מנה טבעונית",
  glattKosher: "גלאט כשר",
  glutenFree: "ללא גלוטן",
  childMeal: "מנות ילדים",
  gender: "גברים / נשים",
};

export const DEFAULT_RSVP_OPTIONS = {
  transportation: { enabled: false, required: null, departureDetails: "" },
  vegetarian: { enabled: false },
  vegan: { enabled: false },
  glattKosher: { enabled: false },
  glutenFree: { enabled: false },
  childMeal: { enabled: false },
  gender: { enabled: false },
};

export const normalizeRsvpOptions = (value) => {
  const source = value && typeof value === "object" ? value : {};
  return Object.fromEntries(
    Object.entries(DEFAULT_RSVP_OPTIONS).map(([key, defaults]) => [
      key,
      {
        ...defaults,
        ...(source[key] && typeof source[key] === "object" ? source[key] : {}),
        enabled: source[key]?.enabled === true,
        ...(key === "transportation" ? {
          required: typeof source[key]?.required === "boolean" ? source[key].required : null,
          departureDetails: typeof source[key]?.departureDetails === "string" ? source[key].departureDetails : "",
        } : {}),
      },
    ])
  );
};

export const getEnabledFoodOptions = (options) => {
  const normalized = normalizeRsvpOptions(options);
  return FOOD_OPTION_KEYS.filter((key) => normalized[key].enabled);
};

export const hasEnabledRsvpOptions = (options) =>
  Object.values(normalizeRsvpOptions(options)).some((option) => option.enabled);

export const createEmptyAttendee = (index) => ({
  id: `attendee-${index + 1}`,
  childMeal: false,
  gender: "",
  foodAnswered: false,
  answeredFoodOptions: [],
  vegetarian: false,
  vegan: false,
  glattKosher: false,
  glutenFree: false,
});

export const createEmptyRsvpDetails = (partySize = 1) => ({
  version: 1,
  partySize,
  completedAt: "",
  answeredFoodOptions: [],
  foodPreferencesAcknowledged: false,
  transportation: {
    answered: false,
    needsRide: false,
    seats: 0,
  },
  attendees: Array.from({ length: partySize }, (_, index) => createEmptyAttendee(index)),
});

export const normalizeRsvpDetails = (value, partySize = 1) => {
  const empty = createEmptyRsvpDetails(partySize);
  if (!value || typeof value !== "object") return empty;
  const attendees = Array.from({ length: partySize }, (_, index) => {
    const storedAttendee = Array.isArray(value.attendees) ? value.attendees[index] : null;
    return {
      ...createEmptyAttendee(index),
      ...(storedAttendee || {}),
      id: storedAttendee?.id || `attendee-${index + 1}`,
      foodAnswered: typeof storedAttendee?.foodAnswered === "boolean"
        ? storedAttendee.foodAnswered
        : Boolean(storedAttendee && value.completedAt && value.foodPreferencesAcknowledged),
      answeredFoodOptions: Array.isArray(storedAttendee?.answeredFoodOptions)
        ? storedAttendee.answeredFoodOptions
        : storedAttendee && Array.isArray(value.answeredFoodOptions)
          ? value.answeredFoodOptions
          : [],
    };
  });
  return {
    ...empty,
    ...value,
    version: 1,
    partySize,
    answeredFoodOptions: Array.isArray(value.answeredFoodOptions) ? value.answeredFoodOptions : [],
    transportation: {
      ...empty.transportation,
      ...(value.transportation && typeof value.transportation === "object" ? value.transportation : {}),
    },
    attendees,
  };
};

export const resizeRsvpDetails = (value, partySize = 1) => {
  const details = normalizeRsvpDetails(value, partySize);
  const transportation = details.transportation?.needsRide
    ? { ...details.transportation, seats: Math.min(Number(details.transportation.seats || 1), partySize) }
    : details.transportation;
  return { ...details, partySize, transportation };
};

export const hasStoredRsvpDetails = (contact) => Boolean(contact?.rsvpDetails?.completedAt);

export const getAttendeePreferenceLabels = (attendee, options, { includeInactive = false } = {}) => {
  const normalized = normalizeRsvpOptions(options);
  const isVisible = (key) => includeInactive || normalized[key].enabled;
  const labels = [];
  const enabledFood = getEnabledFoodOptions(normalized);
  if (isVisible("childMeal") && attendee?.childMeal === true) labels.push("מנות ילדים");
  if (isVisible("vegetarian") && attendee?.vegetarian) labels.push("צמחוני");
  if (isVisible("vegan") && attendee?.vegan) labels.push("טבעוני");
  if (isVisible("glattKosher") && attendee?.glattKosher) labels.push("גלאט כשר");
  if (isVisible("glutenFree") && attendee?.glutenFree) labels.push("ללא גלוטן");
  if (enabledFood.length > 0 && attendee?.foodAnswered === true && !enabledFood.some((key) => attendee?.[key] === true)) {
    labels.push("מנה רגילה");
  }
  if (isVisible("gender") && attendee?.gender === "male") labels.push("גבר");
  if (isVisible("gender") && attendee?.gender === "female") labels.push("אישה");
  return labels;
};

export const getAttendeeExactDish = (attendeeValue, storedAttendeeValue, optionsValue) => {
  const options = normalizeRsvpOptions(optionsValue);
  const enabledFood = getEnabledFoodOptions(options);
  if (!options.childMeal.enabled && enabledFood.length === 0) {
    return { label: "", assumed: false };
  }
  const attendee = attendeeValue && typeof attendeeValue === "object" ? attendeeValue : createEmptyAttendee(0);
  const childMealAnswered = !options.childMeal.enabled || typeof storedAttendeeValue?.childMeal === "boolean";
  const answeredFoodKeys = enabledFood.filter((key) =>
    attendee.foodAnswered === true && attendee.answeredFoodOptions?.includes(key)
  );
  const selectedFoodKeys = answeredFoodKeys.filter((key) => attendee[key] === true);
  const mealType = options.childMeal.enabled
    ? childMealAnswered && attendee.childMeal === true ? "מנת ילדים" : "מנת מבוגר"
    : "מנה";
  const dietaryDescription = selectedFoodKeys.length > 0
    ? selectedFoodKeys.map((key) => FOOD_DISH_LABELS[key]).join(" + ")
    : enabledFood.length > 0 ? "רגילה" : "";
  return {
    label: [mealType, dietaryDescription].filter(Boolean).join(" "),
    assumed: !childMealAnswered || answeredFoodKeys.length < enabledFood.length,
  };
};

export const getContactPreferenceCount = (contact, options) => {
  if (contact?.status !== "approved") return 0;
  const details = normalizeRsvpDetails(contact?.rsvpDetails, parseInt(contact?.howMany, 10) || 1);
  return details.attendees.filter((attendee) =>
    getAttendeePreferenceLabels(attendee, options).some((label) =>
      !["מנות ילדים", "מנה רגילה", "גבר", "אישה"].includes(label)
    )
  ).length;
};

export const getContactOptionCount = (contact, key) => {
  if (contact?.status !== "approved") return 0;
  const details = normalizeRsvpDetails(contact?.rsvpDetails, parseInt(contact?.howMany, 10) || 1);
  if (key === "transportation") return details.transportation?.needsRide ? Number(details.transportation.seats || 0) : 0;
  if (key === "male" || key === "female") {
    return details.attendees.filter((attendee) => attendee.gender === key).length;
  }
  return details.attendees.filter((attendee) => attendee?.[key] === true).length;
};

export const validateRsvpDetails = (detailsValue, optionsValue, partySize) => {
  const options = normalizeRsvpOptions(optionsValue);
  const details = normalizeRsvpDetails(detailsValue, partySize);
  const missing = [];
  if (options.transportation.enabled && options.transportation.required && !details.transportation.answered) {
    missing.push("האם אתם צריכים הסעה");
  }
  if (options.transportation.enabled && details.transportation.answered && details.transportation.needsRide) {
    const seats = Number(details.transportation.seats || 0);
    if (seats < 1 || seats > partySize) missing.push(`מספר מקומות בהסעה בין 1 ל-${partySize}`);
  }
  if (options.gender.enabled && details.attendees.some((attendee) => !["male", "female"].includes(attendee.gender))) {
    missing.push("חלוקת גברים ונשים לכל המגיעים");
  }
  return missing.length > 0 ? `יש להשלים: ${missing.join(" • ")}` : "";
};

export const finalizeRsvpDetails = (
  detailsValue,
  optionsValue,
  partySize,
  { acknowledgeFood = true, markAllAttendeesFoodAnswered = acknowledgeFood } = {}
) => {
  const options = normalizeRsvpOptions(optionsValue);
  const details = normalizeRsvpDetails(detailsValue, partySize);
  const enabledFood = getEnabledFoodOptions(options);
  return {
    ...details,
    completedAt: new Date().toISOString(),
    attendees: markAllAttendeesFoodAnswered && enabledFood.length > 0
      ? details.attendees.map((attendee) => ({
          ...attendee,
          foodAnswered: true,
          answeredFoodOptions: Array.from(new Set([
            ...attendee.answeredFoodOptions,
            ...enabledFood,
          ])),
        }))
      : details.attendees,
    foodPreferencesAcknowledged: acknowledgeFood && enabledFood.length > 0
      ? true
      : details.foodPreferencesAcknowledged,
    answeredFoodOptions: acknowledgeFood
      ? Array.from(new Set([...details.answeredFoodOptions, ...enabledFood]))
      : details.answeredFoodOptions,
  };
};

export const getFoodAnswerState = (contact, optionsValue) => {
  const enabled = getEnabledFoodOptions(optionsValue);
  if (enabled.length === 0) return "disabled";
  const details = contact?.rsvpDetails;
  if (!details?.completedAt) return "notAnswered";
  const answered = Array.isArray(details.answeredFoodOptions) ? details.answeredFoodOptions : [];
  if (enabled.some((key) => !answered.includes(key))) return "notAnswered";
  const partySize = parseInt(String(contact?.howMany || "1"), 10) || 1;
  const normalizedDetails = normalizeRsvpDetails(details, partySize);
  if (normalizedDetails.attendees.some((attendee) =>
    attendee.foodAnswered !== true ||
    enabled.some((key) => !attendee.answeredFoodOptions.includes(key))
  )) return "notAnswered";
  return getContactPreferenceCount(contact, optionsValue) > 0 ? "hasPreferences" : "none";
};

export const getContactRsvpMissingAnswers = (contact, optionsValue) => {
  const options = normalizeRsvpOptions(optionsValue);
  const partySize = parseInt(String(contact?.howMany || "1"), 10) || 1;
  const missing = { transportation: 0, childMeal: 0, gender: 0, foodPreferences: 0 };
  if (contact?.status !== "approved") return missing;

  const hasDetails = hasStoredRsvpDetails(contact);
  const details = normalizeRsvpDetails(contact?.rsvpDetails, partySize);
  if (options.transportation.enabled && !details.transportation.answered) missing.transportation = 1;
  const enabledFood = getEnabledFoodOptions(options);
  if (enabledFood.length > 0 && getFoodAnswerState(contact, options) === "notAnswered") {
    missing.foodPreferences = details.attendees.filter((attendee) =>
      attendee.foodAnswered !== true ||
      enabledFood.some((key) => !attendee.answeredFoodOptions.includes(key))
    ).length;
  }
  // Voice may answer gender without completing the other RSVP options.
  if (options.gender.enabled) {
    missing.gender = details.attendees.filter((attendee) => !["male", "female"].includes(attendee.gender)).length;
  }
  if (!hasDetails) {
    if (options.childMeal.enabled) missing.childMeal = partySize;
    return missing;
  }
  for (let attendeeIndex = 0; attendeeIndex < details.attendees.length; attendeeIndex += 1) {
    const storedAttendee = contact?.rsvpDetails?.attendees?.[attendeeIndex];
    if (options.childMeal.enabled && typeof storedAttendee?.childMeal !== "boolean") missing.childMeal += 1;
  }
  return missing;
};

export const hasMissingRsvpAnswers = (contact, optionsValue) =>
  Object.values(getContactRsvpMissingAnswers(contact, optionsValue)).some((count) => count > 0);

export const getEventRsvpSummary = (contactsValue, optionsValue) => {
  const options = normalizeRsvpOptions(optionsValue);
  const enabledFood = getEnabledFoodOptions(options);
  const approvedContacts = (Array.isArray(contactsValue) ? contactsValue : [])
    .filter((contact) => contact?.status === "approved");
  const totals = {
    approvedPeople: 0,
    transportationReservations: 0,
    transportationSeats: 0,
    childMeal: 0,
    adultMeal: 0,
    male: 0,
    female: 0,
    vegetarian: 0,
    vegan: 0,
    glattKosher: 0,
    glutenFree: 0,
    noFoodPreferences: 0,
  };
  const missing = { transportation: 0, childMeal: 0, gender: 0, foodPreferences: 0 };
  const combinations = new Map();
  const dishCombinations = new Map();

  const addDishCombination = (label, assumed) => {
    if (!label) return;
    const current = dishCombinations.get(label) || { count: 0, assumedCount: 0 };
    dishCombinations.set(label, {
      count: current.count + 1,
      assumedCount: current.assumedCount + (assumed ? 1 : 0),
    });
  };

  approvedContacts.forEach((contact) => {
    const partySize = parseInt(String(contact?.howMany || "1"), 10) || 1;
    totals.approvedPeople += partySize;
    const details = normalizeRsvpDetails(contact?.rsvpDetails, partySize);
    const contactMissing = getContactRsvpMissingAnswers(contact, options);
    Object.keys(missing).forEach((key) => { missing[key] += contactMissing[key]; });

    if (options.transportation.enabled) {
      if (details.transportation.answered && details.transportation.needsRide) {
        totals.transportationReservations += 1;
        totals.transportationSeats += Number(details.transportation.seats || 0);
      }
    }

    details.attendees.forEach((attendee, attendeeIndex) => {
      if (options.childMeal.enabled) {
        if (attendee.childMeal === true) totals.childMeal += 1;
        if (attendee.childMeal === false) totals.adultMeal += 1;
      }
      if (options.gender.enabled) {
        if (attendee.gender === "male") totals.male += 1;
        if (attendee.gender === "female") totals.female += 1;
      }
      enabledFood.forEach((key) => {
        if (attendee[key] === true) totals[key] += 1;
      });

      const selectedFoodKeys = enabledFood.filter((key) => attendee[key] === true);
      const foodLabels = selectedFoodKeys.map((key) => RSVP_OPTION_LABELS[key]);
      const attendeeFoodAnswered = attendee.foodAnswered === true &&
        enabledFood.every((key) => attendee.answeredFoodOptions.includes(key));
      if (enabledFood.length > 0 && foodLabels.length === 0 && attendeeFoodAnswered) totals.noFoodPreferences += 1;
      const storedAttendee = contact?.rsvpDetails?.attendees?.[attendeeIndex];
      const childMealAnswered = !options.childMeal.enabled || typeof storedAttendee?.childMeal === "boolean";
      const allFoodAnswered = enabledFood.length === 0 || attendeeFoodAnswered;
      const exactDish = getAttendeeExactDish(attendee, storedAttendee, options);
      addDishCombination(exactDish.label, exactDish.assumed);
      const combinationParts = [
        ...(options.gender.enabled && attendee.gender ? [attendee.gender === "male" ? "גבר" : "אישה"] : []),
        ...(exactDish.label ? [exactDish.label] : []),
        ...(options.childMeal.enabled && !childMealAnswered ? ["בחירת מנת ילד/מבוגר טרם נבחרה"] : []),
        ...(enabledFood.length > 0 && !allFoodAnswered ? ["העדפות מזון טרם נבחרו"] : []),
      ];
      if (combinationParts.length > 0) {
        const combination = combinationParts.join(" + ");
        combinations.set(combination, (combinations.get(combination) || 0) + 1);
      }
    });
  });

  return {
    totals,
    missing,
    combinations: Array.from(combinations.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, "he")),
    dishCombinations: Array.from(dishCombinations.entries())
      .map(([label, values]) => ({ label, ...values }))
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, "he")),
  };
};
